import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { FONT_MONO } from '../theme';
import { Button, Card } from '../ui';
import { CardTitle, PillGroup } from './common';
import { fmtInt } from './nutritionData';
import { dayLabel, shortDate } from './statsData';

const METRICS = [
  { id: 'kcal', label: 'kcal' },
  { id: 'protein', label: 'Protein' },
];

const STATUS_WORDS = { complete: 'complete', partial: 'partial', untracked: 'not tracked', unknown: 'nothing logged' };

/* Fill of one bar. Days that don't count are hatched (partial) or only
 * outlined (not tracked), so they read as "not a real number" next to the
 * coloured ones; a solid grey would look like an "under" day. */
function barStyle(t, band) {
  switch (band) {
    case 'on': return { background: t.accent };
    case 'over': return { background: t.danger };
    case 'under': return { background: t.textMuted, opacity: 0.55 };
    case 'partial': return {
      background: `repeating-linear-gradient(135deg, ${t.textFaint} 0 2px, transparent 2px 5px)`,
      boxShadow: `inset 0 0 0 1px ${t.textFaint}`,
    };
    case 'untracked': return { background: 'transparent', border: `1px dashed ${t.textFaint}` };
    default: return { background: t.divider };
  }
}

function Swatch({ t, band, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, display: 'inline-block', ...barStyle(t, band) }} />
      {children}
    </span>
  );
}

/* Daily kcal (or protein) for the 28 days to yesterday against the target.
 * Bars are ~11 px wide on a phone, too narrow to hit reliably, so a tap
 * anywhere in a day's column selects it and the detail line under the chart
 * opens that day in Meals. */
export function NutritionChartCard({ t, rows, targets, onOpenDay }) {
  const [metric, setMetric] = useState('kcal');
  const [selected, setSelected] = useState(null);
  const target = metric === 'protein' ? targets.protein : targets.kcal;
  const value = r => (metric === 'protein' ? r.protein : r.kcal);
  const band = r => (metric === 'protein' ? r.proteinBand : r.band);
  const top = Math.max(target * 1.25, ...rows.map(value), 1) * 1.05;
  const pct = v => `${Math.max(0, Math.min(100, (v / top) * 100))}%`;
  const unit = metric === 'protein' ? 'g' : 'kcal';
  const sel = rows.find(r => r.date === selected) || null;
  const blocks = [0, 7, 14, 21].filter(i => rows[i]);
  const anyUntracked = rows.some(r => r.band === 'untracked');

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t} sub={`Last ${rows.length} days to yesterday. Hatched days are partial and left out of averages.`}
        right={<PillGroup t={t} label="Chart metric" options={METRICS} value={metric} onChange={setMetric} />}>
        Daily intake
      </CardTitle>

      <div style={{ position: 'relative', height: 150 }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', gap: 2 }}>
          {rows.map(r => {
            const b = band(r);
            const active = r.date === selected;
            return (
              <button key={r.date} type="button" aria-pressed={active}
                aria-label={`${dayLabel(r.date)}: ${r.items ? `${fmtInt(value(r))} ${unit}, ${STATUS_WORDS[r.status] || r.status}` : 'nothing logged'}`}
                onClick={() => setSelected(active ? null : r.date)}
                style={{
                  flex: 1, minWidth: 0, height: '100%', padding: 0, border: 'none', cursor: 'pointer',
                  borderRadius: 4, background: active ? t.surfaceAlt : 'transparent',
                  display: 'flex', alignItems: 'flex-end',
                }}>
                <span style={{
                  display: 'block', width: '100%', borderRadius: '3px 3px 0 0',
                  height: b === 'empty' ? 2 : pct(value(r)),
                  outline: active ? `2px solid ${t.text}` : 'none', outlineOffset: 1,
                  ...barStyle(t, b),
                }} />
              </button>
            );
          })}
        </div>
        {target > 0 && (
          <>
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: pct(target), height: 0,
              borderTop: `1px dashed ${t.textMuted}`, pointerEvents: 'none',
            }} />
            <span style={{
              position: 'absolute', right: 0, bottom: `calc(${pct(target)} + 3px)`, pointerEvents: 'none',
              fontSize: 10, fontFamily: FONT_MONO, fontWeight: 600, color: t.textMuted,
              background: t.cardBg, padding: '0 4px', borderRadius: 4,
            }}>{metric === 'protein' ? `${fmtInt(target)} g` : fmtInt(target)}</span>
          </>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${blocks.length || 1}, minmax(0, 1fr))`, marginTop: 4 }}>
        {blocks.map(i => (
          <span key={i} style={{ fontSize: 10, color: t.textFaint, borderLeft: `1px solid ${t.divider}`, paddingLeft: 3 }}>
            {shortDate(rows[i].date)}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11, color: t.textMuted, marginTop: 10 }}>
        {metric === 'protein' ? (
          <>
            <Swatch t={t} band="on">90%+ of target</Swatch>
            <Swatch t={t} band="under">under</Swatch>
          </>
        ) : (
          <>
            <Swatch t={t} band="on">within 10%</Swatch>
            <Swatch t={t} band="under">under</Swatch>
            <Swatch t={t} band="over">over</Swatch>
          </>
        )}
        <Swatch t={t} band="partial">partial</Swatch>
        {anyUntracked && <Swatch t={t} band="untracked">not tracked</Swatch>}
      </div>

      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: `1px solid ${t.divider}`, minHeight: 40,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      }}>
        {sel ? (
          <>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{dayLabel(sel.date)}</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                {sel.items
                  ? `${fmtInt(sel.kcal)} kcal · ${fmtInt(sel.protein)} g P · ${STATUS_WORDS[sel.status] || sel.status}`
                  : 'nothing logged'}
              </div>
            </div>
            {onOpenDay && (
              <Button t={t} onClick={() => onOpenDay(sel.date)} style={{ minHeight: 40, flexShrink: 0 }}>
                Open in Meals <ChevronRight size={14} />
              </Button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: t.textFaint }}>Tap a day to see it and open it in Meals.</div>
        )}
      </div>
    </Card>
  );
}
